import { Text } from "@prismicio/editor-ui"

import {
  frameworkCreateProjectCodeSnippet,
  frameworkLabels,
} from "../../constants/framework"
import { DeferredCodeBlock as CodeBlock } from "../DeferredCodeBlock"
import type { OnboardingFramework, OnboardingStep } from "../service/onboarding"

// NOTE: content in code is mvp solution.
// To avoid unsync of the content if editor-fields version is different
// between SM and PB the content might be moved to the back-end in the future

type Context = {
  framework?: OnboardingFramework
  starterId?: string | null
  repositoryName?: string
  projectName?: string
}

export function getOnboardingContent(context: Context) {
  const { framework, projectName, starterId, repositoryName } = context

  const { frameworkNameLabel, createProjectCodeSnippet } = getLabels(context)

  const onboardingSteps: OnboardingStep[] = [
    {
      id: "createPrismicProject",
      title: "Create your Prismic project",
      description: "Create and set up your project",
      defaultCompleted: true,
      content: (
        <Text>
          A Prismic project is a space for managing and organizing the content
          of your website.
        </Text>
      ),
    },
    {
      id: "chooseLocale",
      title: "Select your main locale",
      description: "Choose your first locale",
      defaultCompleted: Boolean(starterId),
      content: (
        <Text>
          Locales allow you to create and manage multilingual versions of your
          content within a single Prismic project.
        </Text>
      ),
    },
    {
      id: "createProject",
      title: `Create a ${frameworkNameLabel}project`,
      description: "Create and set up your project",
      videoUrl:
        "https://res.cloudinary.com/dmtf1daqp/video/upload/v1727768069/DEV_TOOLS/ONBOARDING_GUIDE/Pagebuilder_onboarding_step_3_puh6fx.mp4",
      content: (
        <>
          <Text sx={{ marginBottom: 16 }}>
            Now that you have a Prismic project to manage the content of your
            website, let's create the code project that will be querying your
            content.
          </Text>
          {!!createProjectCodeSnippet && (
            <CodeBlock
              code={`${createProjectCodeSnippet} ${
                repositoryName ?? "<my-project>"
              }`}
              language="bash"
            />
          )}
          <Text variant="small" sx={{ marginTop: 16 }}>
            (example for Next.js below)
          </Text>
        </>
      ),
    },
    {
      id: "setupSliceMachine",
      title: "Set up & run Slice Machine",
      description: "Prepare the code to fetch content",
      videoUrl:
        "https://res.cloudinary.com/dmtf1daqp/video/upload/v1727768069/DEV_TOOLS/ONBOARDING_GUIDE/page_builder_onboarding_step_4_yl8fb8.mp4",
      content: (
        <>
          <Text sx={{ marginBottom: 16 }}>
            {`Slice Machine is the Prismic tool for creating your content models.
          Install it in your ${frameworkNameLabel}project by running these commands:`}
          </Text>
          <CodeBlock
            code={`cd ${projectName ?? "<your project name>"}
npx @slicemachine/init@latest --repository ${repositoryName ?? "<my-project>"}`}
            language="bash"
            title="Terminal"
            showLineNumbers={false}
          />
        </>
      ),
    },
    {
      id: "createPageType",
      title: "Create a Page Type",
      description: "Build the structure of your page",
      videoUrl:
        "https://res.cloudinary.com/dmtf1daqp/video/upload/v1721918320/DEV_TOOLS/ONBOARDING_GUIDE/Create_page_type_xdn13j.mp4",
      content: (
        <Text>
          A page type is a base content structure editors will use to create
          pages in the Page Builder (Prismic's content creation UI). A page type
          can be reusable (e.g., for multiple blog posts) or single (e.g., for a
          one-time page like the homepage).
        </Text>
      ),
    },
    {
      id: "codePage",
      title: "Code Your Page Type",
      description: "Prepare code to fetch content",
      videoUrl:
        "https://res.cloudinary.com/dmtf1daqp/video/upload/v1721918320/DEV_TOOLS/ONBOARDING_GUIDE/Step2_code_your_page_jatiur.mp4",
      content: (
        <Text>
          Prepare your code to query the content from the Prismic API.
        </Text>
      ),
    },
    {
      id: "createSlice",
      title: "Create a Slice",
      description: "Build a reusable website section",
      videoUrl:
        "https://res.cloudinary.com/dmtf1daqp/video/upload/v1721918322/DEV_TOOLS/ONBOARDING_GUIDE/Step3_add_slice_qzmvxf.mp4",
      content: (
        <Text>
          Slices are website sections that can be reused on different pages with
          different content. Each slice has a code component automatically
          generated by Slice Machine. Start with a template and look at your
          code to see how it's structured.
        </Text>
      ),
    },
    {
      id: "reviewAndPush",
      title: "Review and Push changes",
      description: "Enable editors to create content",
      videoUrl:
        "https://res.cloudinary.com/dmtf1daqp/video/upload/v1721918320/DEV_TOOLS/ONBOARDING_GUIDE/DevTools_Squad_push_changes_ovvmul.mp4",
      content: (
        <Text>
          Your page types and slices currently exist only in your local project.
          Push them to your repository to make them available for your content
          editors.
        </Text>
      ),
    },
    {
      id: "createContent",
      title: "Create Content",
      description: "Publish a page to the Prismic API",
      videoUrl:
        "https://res.cloudinary.com/dmtf1daqp/video/upload/v1732631905/DEV_TOOLS/ONBOARDING_GUIDE/step_9_create_content_y8m3m0.mp4",
      content: (
        <Text>
          In Prismic, content creation takes place in the Prismic Page Builder
          UI. Open the Page Builder and start creating your first page.
        </Text>
      ),
    },
    {
      id: "renderPage",
      title: "Render Your Page",
      description: "View your page in the browser",
      videoUrl:
        "https://res.cloudinary.com/dmtf1daqp/video/upload/v1721918321/DEV_TOOLS/ONBOARDING_GUIDE/Step_6_Render_Page_tnauh9.mp4",
      content: (
        <Text>
          Now that your content is published, you can run your project in your
          terminal and visit the page on your local server (e.g.,{" "}
          <Text component="code">localhost:3000/example-page</Text>). Your
          content should now be visible!
        </Text>
      ),
    },
  ]

  const tutorialLinks: Partial<Record<OnboardingFramework, string>> = {
    next: "https://prismic.dev/course/next",
    nuxt: "https://prismic.dev/course/nuxt",
    sveltekit: "https://prismic.dev/course/sveltekit",
  }

  return {
    steps: onboardingSteps,
    defaultCompletedStepIds: onboardingSteps.flatMap((step) =>
      step.defaultCompleted ? [step.id] : [],
    ),
    tutorialLink: framework ? tutorialLinks[framework] : undefined,
  }
}

function getLabels(context: Context) {
  const { framework } = context
  if (!framework || framework === "other") {
    return { frameworkNameLabel: "", createProjectCodeSnippet: "" }
  }

  const frameworkName = frameworkLabels[framework]
  return {
    frameworkNameLabel: frameworkName ? `${frameworkName} ` : "",
    createProjectCodeSnippet: frameworkCreateProjectCodeSnippet[framework],
  }
}
